/**
 * ForgotPassword & ResetPassword
 * ===============================
 * Two un-authenticated pages that power the self-service password
 * reset flow:
 *
 *   /forgot-password    →  email input → backend issues a reset link
 *   /reset-password?token=…  →  user picks a new password
 *
 * Both pages reuse the existing `.auth-*` styles so they look at home
 * next to the Login page, and never reveal whether an email exists in
 * the database (the server returns a constant 200; we display the same
 * "If an account exists…" success copy in every case).
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';

// ============================================================
// /forgot-password
// ============================================================

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter the email on your account.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await api.forgotPassword(trimmed);
      setSent(true);
    } catch {
      // The endpoint is uniform-200 by design, so any thrown error here
      // is genuine (network/CORS). Show a generic message.
      setError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-container" data-testid="forgot-password-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1>🔐 Forgot password</h1>
          <p>
            Enter the email on your Kids WiseApp account and we'll send you
            a link to reset your password.
          </p>
        </div>

        {sent ? (
          <div data-testid="forgot-password-sent-state" style={sentBoxStyle}>
            <div style={{ fontSize: '2.25rem', marginBottom: '0.5rem' }}>📬</div>
            <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Check your inbox</h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', lineHeight: 1.55 }}>
              If an account exists for <strong>{email}</strong>, a reset
              link is on the way. The link expires in 60&nbsp;minutes and
              can be used only once.
            </p>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
              Not seeing it? Wait a minute, then check your spam folder.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18, flexWrap: 'wrap' }}>
              <Link to="/login" className="btn btn-primary" data-testid="forgot-password-back-to-login" style={{ textDecoration: 'none' }}>
                Back to sign in
              </Link>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setSent(false); setEmail(''); }}
                data-testid="forgot-password-try-again-btn"
              >
                Try a different email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form" data-testid="forgot-password-form">
            <div className="form-group">
              <label htmlFor="forgot-email">Email</label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
                data-testid="forgot-password-email-input"
                placeholder="you@example.com"
              />
            </div>

            {error && (
              <div data-testid="forgot-password-error" style={errorStyle}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
              data-testid="forgot-password-submit-btn"
              style={{ width: '100%' }}
            >
              {busy ? 'Sending…' : 'Email me a reset link'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.86rem' }}>
              <Link
                to="/login"
                data-testid="forgot-password-back-link"
                style={{ color: 'hsl(var(--primary))', textDecoration: 'none', fontWeight: 600 }}
              >
                ← Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================
// /reset-password?token=…
// ============================================================

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();

  const [validating, setValidating] = useState(true);
  const [tokenState, setTokenState] = useState({ valid: false });
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Pre-flight validate so we can show a useful message instead of a
  // long path of guessing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) {
          setTokenState({ valid: false, reason: 'missing' });
          setValidating(false);
        }
        return;
      }
      try {
        const data = await api.validateResetToken(token);
        if (!cancelled) {
          setTokenState(data || { valid: false });
          setValidating(false);
        }
      } catch {
        if (!cancelled) {
          setTokenState({ valid: false, reason: 'unreachable' });
          setValidating(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (pw1.length < 8) {
      setError('Your new password must be at least 8 characters.');
      return;
    }
    if (pw1 !== pw2) {
      setError('The two passwords don\'t match.');
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, pw1);
      setSuccess(true);
      // Auto-redirect after a short pause so the user reads the
      // confirmation but isn't left staring at a dead end.
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      setError(err.message || 'Could not reset your password. Please try again or request a new link.');
    } finally {
      setBusy(false);
    }
  }

  if (validating) {
    return (
      <div className="auth-container" data-testid="reset-password-page-validating">
        <div className="auth-card">
          <div className="auth-header">
            <h1>🔐 Reset password</h1>
            <p>Verifying your reset link…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenState.valid) {
    const reasonMap = {
      missing:    'No reset token was provided in the link.',
      not_found:  'This reset link is invalid.',
      used:       'This reset link has already been used.',
      expired:    'This reset link has expired.',
      unreachable:"We couldn't reach the server to verify your link.",
    };
    const message = reasonMap[tokenState.reason] || 'This reset link can\'t be used.';
    return (
      <div className="auth-container" data-testid="reset-password-invalid-state">
        <div className="auth-card">
          <div className="auth-header">
            <h1>🔐 Reset password</h1>
            <p>{message}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
            <Link
              to="/forgot-password"
              className="btn btn-primary"
              data-testid="reset-password-request-new-link"
              style={{ textDecoration: 'none', textAlign: 'center' }}
            >
              Request a new reset link
            </Link>
            <Link
              to="/login"
              data-testid="reset-password-back-to-login"
              style={{ textAlign: 'center', color: 'hsl(var(--primary))', textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem' }}
            >
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container" data-testid="reset-password-success-state">
        <div className="auth-card">
          <div className="auth-header">
            <h1>✅ Password updated</h1>
            <p>
              Your new password is ready. Redirecting you to sign in…
            </p>
          </div>
          <Link
            to="/login"
            className="btn btn-primary"
            data-testid="reset-password-go-signin"
            style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
          >
            Sign in now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" data-testid="reset-password-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1>🔐 Set a new password</h1>
          <p>
            {tokenState.email_masked ? (
              <>Choose a new password for <strong>{tokenState.email_masked}</strong>.</>
            ) : (
              <>Choose a new password for your account.</>
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" data-testid="reset-password-form">
          <div className="form-group">
            <label htmlFor="reset-pw1">New password</label>
            <input
              id="reset-pw1"
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              autoFocus
              required
              data-testid="reset-password-new-input"
            />
            <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>
              At least 8 characters.
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="reset-pw2">Confirm new password</label>
            <input
              id="reset-pw2"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              required
              data-testid="reset-password-confirm-input"
            />
          </div>

          {error && (
            <div data-testid="reset-password-error" style={errorStyle}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy}
            data-testid="reset-password-submit-btn"
            style={{ width: '100%' }}
          >
            {busy ? 'Updating…' : 'Update password'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.86rem' }}>
            <Link
              to="/login"
              data-testid="reset-password-cancel-link"
              style={{ color: 'hsl(var(--muted-foreground))', textDecoration: 'none', fontWeight: 600 }}
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Shared inline styles
// ============================================================

const errorStyle = {
  padding: '0.6rem 0.85rem',
  background: 'hsl(0 70% 96%)',
  border: '1px solid hsl(0 70% 80%)',
  borderRadius: 'var(--radius)',
  color: 'hsl(0 60% 35%)',
  fontSize: '0.85rem',
  marginBottom: '0.75rem',
};

const sentBoxStyle = {
  textAlign: 'center',
  padding: '1rem 0.5rem 0',
};
